/**
 * The claims lens.
 *
 * This is the one domain-specific view in the console, and it mounts only when a
 * document actually produced loss rows. Everything else on the detail screen is true of
 * any document; this is true of loss runs, so it appears for loss runs and nowhere else.
 *
 * It carries the money rules that the rest of the product enforces silently. `null`
 * cents render *unknown*, never `$0.00` — a fabricated zero both breaks the incurred
 * identity and scores as a correct read. The identity is recomputed in front of the
 * reader, and when a component is unknown it reports that it cannot be checked rather
 * than showing a pass.
 */
import { useState } from "react";
import { money } from "../lib/format";
import { isCorrectable, type LossRow } from "../lib/types";
import { CorrectField } from "./CorrectField";
import { Crop } from "./Crop";
import { Button, Chip, METHOD_TONE } from "./ui";

function Field({
  name,
  children,
  tone = "",
  rowId,
  correctAs,
  current,
}: {
  name: string;
  children: React.ReactNode;
  tone?: string;
  rowId?: string;
  /** The server-side column name, which is not always the label shown. */
  correctAs?: string;
  current?: string;
}) {
  const editable = rowId && correctAs && isCorrectable(correctAs);
  return (
    <div className="grid grid-cols-[136px_minmax(0,1fr)_auto] items-baseline gap-3 border-b border-hair-soft px-4 py-2">
      <span className="truncate font-mono text-[10.5px] tracking-[0.08em] text-faint">{name}</span>
      <span className={`min-w-0 text-[13px] ${tone}`}>{children}</span>
      {editable ? (
        <CorrectField rowId={rowId} field={correctAs} current={current ?? ""} />
      ) : (
        <span />
      )}
    </div>
  );
}

/** What to prefill a money edit with: the printed figure, never the raw cents. */
function editable(cents: number | null): string {
  return cents === null ? "" : (cents / 100).toFixed(2);
}

/** Unknown is a value, and it says why. It is never rendered as zero. */
function Money({ cents }: { cents: number | null }) {
  const shown = money(cents);
  if (shown === null) {
    return (
      <span className="text-warn">
        unknown{" "}
        <span className="font-sans text-[11.5px] text-faint">blank or unparsable — not zero</span>
      </span>
    );
  }
  return (
    <span className="tnum font-mono font-medium">
      {shown} <span className="font-sans text-[11px] text-faint">{cents}</span>
    </span>
  );
}

type Check =
  | { state: "ok" }
  | { state: "off"; computed: number; delta: number }
  | { state: "unknown" };

/** paid + reserve = incurred, but only when every part is known. */
function identity(row: LossRow): Check {
  const parts = [
    row.paid_indemnity_cents,
    row.paid_expense_cents,
    row.reserve_indemnity_cents,
    row.reserve_expense_cents,
  ];
  if (parts.some((part) => part === null) || row.incurred_cents === null) return { state: "unknown" };
  const computed = parts.reduce<number>((sum, part) => sum + (part as number), 0);
  const delta = computed - row.incurred_cents;
  return delta === 0 ? { state: "ok" } : { state: "off", computed, delta };
}

export function ClaimsLens({ rows }: { rows: LossRow[] }) {
  const [index, setIndex] = useState(0);
  const row = rows[Math.min(index, rows.length - 1)];
  if (!row) return null;

  const check = identity(row);

  return (
    <div>
      <div className="flex items-center gap-2 border-b border-hair px-4 py-1.5">
        <Button variant="ghost" disabled={index === 0} onClick={() => setIndex((i) => Math.max(0, i - 1))}>
          ← prev
        </Button>
        <span className="tnum font-mono text-[11px] tracking-[0.1em] text-faint uppercase">
          claim {index + 1} / {rows.length}
        </span>
        <Button
          variant="ghost"
          disabled={index >= rows.length - 1}
          onClick={() => setIndex((i) => Math.min(rows.length - 1, i + 1))}
        >
          next →
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-hair bg-hair-soft px-4 py-2">
        <Chip tone="gold">{row.claim_number}</Chip>
        <span className="font-mono text-[11px] text-dim">{row.date_of_loss}</span>
        <Chip tone={METHOD_TONE[row.source.method] ?? "mut"}>{row.source.method.replace("_", " ")}</Chip>
        {/* provenance counts pages from zero; every printed document counts from one */}
        <span className="ms-auto font-mono text-[10.5px] text-faint">page {row.source.page + 1}</span>
      </div>

      <Field name="claim_number" rowId={row.id} correctAs="claim_number" current={row.claim_number}>
        <span className="font-mono">{row.claim_number}</span>
      </Field>
      <Field name="policy_number">
        {row.policy_number ? <span className="font-mono">{row.policy_number}</span> : <span className="text-faint">—</span>}
      </Field>
      <Field name="policy_year">
        <span className="tnum font-mono">{row.policy_year}</span>
      </Field>
      <Field name="date_of_loss" rowId={row.id} correctAs="date_of_loss" current={row.date_of_loss}>
        <span className="font-mono">{row.date_of_loss}</span>
      </Field>
      <Field
        name="date_reported"
        rowId={row.id}
        correctAs="date_reported"
        current={row.date_reported ?? ""}
      >
        {row.date_reported ? (
          <span className="font-mono">{row.date_reported}</span>
        ) : (
          <span className="text-warn">
            unknown{" "}
            <span className="font-sans text-[11.5px] text-faint">
              not printed — never inferred from the loss date
            </span>
          </span>
        )}
      </Field>
      <Field name="status" rowId={row.id} correctAs="status" current={row.status}>
        {row.status}
      </Field>
      <Field name="cause">{row.cause ?? <span className="text-faint">—</span>}</Field>
      <Field name="litigation">
        {row.litigation === undefined ? <span className="font-mono text-faint">null</span> : String(row.litigation)}
      </Field>

      <div className="flex items-center gap-3 border-y border-hair bg-hair-soft px-4 py-1.5">
        <span className="eyebrow">// money · integer cents</span>
        <span className="ms-auto font-mono text-[10px] tracking-[0.1em] text-faint">
          paid + reserve = incurred
        </span>
      </div>

      <Field
        name="paid_indemnity"
        rowId={row.id}
        correctAs="paid_indemnity_cents"
        current={editable(row.paid_indemnity_cents)}
      >
        <Money cents={row.paid_indemnity_cents} />
      </Field>
      <Field
        name="paid_expense"
        rowId={row.id}
        correctAs="paid_expense_cents"
        current={editable(row.paid_expense_cents)}
      >
        <Money cents={row.paid_expense_cents} />
      </Field>
      <Field
        name="reserve_indemnity"
        rowId={row.id}
        correctAs="reserve_indemnity_cents"
        current={editable(row.reserve_indemnity_cents)}
      >
        <Money cents={row.reserve_indemnity_cents} />
      </Field>
      <Field name="reserve_expense"><Money cents={row.reserve_expense_cents} /></Field>
      <Field
        name="incurred"
        tone={check.state === "off" ? "text-crit" : ""}
        rowId={row.id}
        correctAs="incurred_cents"
        current={editable(row.incurred_cents)}
      >
        <Money cents={row.incurred_cents} />
      </Field>

      <div className="border-b border-hair px-4 py-3">
        {check.state === "ok" && (
          <p className="text-[12.5px] text-ok">
            <span className="font-mono">&gt;</span> Identity closes — paid plus reserve equals the
            printed incurred exactly.
          </p>
        )}
        {check.state === "off" && (
          <p className="text-[12.5px] text-crit">
            <span className="font-mono">&gt;</span> Identity is off by{" "}
            <span className="tnum font-mono">{money(check.delta)}</span>; the components sum to{" "}
            <span className="tnum font-mono">{money(check.computed)}</span>.
          </p>
        )}
        {check.state === "unknown" && (
          <p className="text-[12.5px] text-warn">
            <span className="font-mono">&gt;</span> Identity cannot be checked — at least one
            component is unknown. Treating a blank as zero here would manufacture a passing check.
          </p>
        )}
      </div>

      {row.flags.length > 0 && (
        <div className="border-b border-hair px-4 py-3">
          <p className="eyebrow mb-2">// flags on this claim</p>
          <ul className="flex flex-col gap-1.5">
            {row.flags.map((flag, i) => (
              <li key={i} className="text-[12.5px] text-dim">
                <span className="font-mono text-[11px] text-gold">{flag.rule}</span> — {flag.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-b border-hair">
        <p className="eyebrow px-4 pt-3 pb-2">// source pixels</p>
        {row.id ? (
          <Crop rowId={row.id} />
        ) : (
          <p className="px-4 pb-3 text-[12px] text-dim">
            This row has no stored id, so there is nothing to fetch a crop for.
          </p>
        )}
      </div>

      <div className="bg-hair-soft px-4 py-3">
        <p className="eyebrow mb-2">// provenance</p>
        <pre className="font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-dim">
{JSON.stringify(row.source, null, 2)}
        </pre>
      </div>
    </div>
  );
}
